import React, { useState } from 'react';
import { css } from 'aphrodite/no-important';
import { Heading } from '@trainline/depot-web';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { FormattedMessage } from 'react-intl';

import {
  FetchJourneyResponse,
  useFetchJourneys,
  ValidatedJourney,
} from '@contactcentre-web/hooks/api/useFetchJourneys';
import Loader from '@contactcentre-web/common/Loader';
import PageContainer from '@contactcentre-web/common/PageContainer';
import Link from '@contactcentre-web/common/Link';

import PeriodSelector from './components/PeriodSelector/PeriodSelector';
import styles from './styles';
import { FareSummary } from './components/FareSummary/FareSummary';
import { JourneyList } from './components/JourneyList/JourneyList';
import { JourneyDetails } from './components/JourneyDetails/JourneyDetails';
import { PaymentSummary } from './components/PaymentSummary/PaymentSummary';
import ProfileStatus from './components/ProfileStatus';
import messages from './messages';

dayjs.extend(isoWeek);

interface Props {
  customerProfileId: string;
}

const getValidatedJourneysForPeriod = (journeys: ValidatedJourney[], startDate: string) =>
  journeys.find((journey) => journey.begin == startDate);

export const PayAsYouGoContainer = ({}: Props) => {
  const params = new URLSearchParams(location.search);
  const journeyId = params.get('journey');
  const { loading, pendingJourneys, validatedJourneys, payment }: FetchJourneyResponse =
    useFetchJourneys();
  const [selectedWeekStartDate, setSelectedWeekStartDate] = useState(
    dayjs().day(1).format('YYYY-MM-DD')
  );

  if (loading) {
    return (
      <div className={css(styles.tabContainer)}>
        <Loader styleSheet={{ container: styles.loader }} />
      </div>
    );
  }

  if (!pendingJourneys && !validatedJourneys) {
    return null;
  }

  const isCurrentWeekSelected = dayjs().isSame(selectedWeekStartDate, 'week');

  const validatedJourneysForPeriod = getValidatedJourneysForPeriod(
    validatedJourneys,
    selectedWeekStartDate
  );

  const lastDayOfSelectedPeriod = dayjs(selectedWeekStartDate).endOf('week').format('YYYY-MM-DD');

  if (journeyId) {
    return (
      <div className={css(styles.tabContainer)}>
        <PageContainer styleSheet={styles.pageContainer}>
          <ProfileStatus />
          <JourneyDetails />
        </PageContainer>
      </div>
    );
  }

  return (
    <div className={css(styles.tabContainer)}>
      <PageContainer styleSheet={styles.pageContainer}>
        <ProfileStatus />
        <>
          <div className={css(styles.headerContainer)}>
            <Heading as="h2" typeStyle="title2">
              <FormattedMessage {...messages.weeklyRollup} />
            </Heading>
            <Link linkType="internal" to="foo" className={css(styles.disputesLink)}>
              <FormattedMessage {...messages.disputes} />
            </Link>
          </div>
          <PeriodSelector
            selectedWeekStartDate={selectedWeekStartDate}
            selectWeekStartDate={setSelectedWeekStartDate}
          />
          {isCurrentWeekSelected && pendingJourneys.length > 0 && (
            <div className={css(styles.pendingJourneys)}>
              <Heading>
                <FormattedMessage {...messages.pendingJourneys} />
              </Heading>
              <JourneyList journeys={pendingJourneys} isPending={true} />
            </div>
          )}
          <FareSummary
            isCurrent={isCurrentWeekSelected}
            validatedJourneys={validatedJourneysForPeriod}
          />
          <PaymentSummary
            payment={payment}
            isCurrent={isCurrentWeekSelected}
            lastDayOfSelectedPeriod={lastDayOfSelectedPeriod}
          />
        </>
      </PageContainer>
    </div>
  );
};

export default PayAsYouGoContainer;
